/**
 * DFCPage
 * 
 * Página de Demonstração do Fluxo de Caixa (DFC).
 * Exibe DFC baseado em extrato bancário real com layout baseado em FinancialReports.md.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { 
  Download, ChevronDown, ChevronRight, ChevronLeft, 
  Wallet, Info, CheckCircle2, Search,
  Clock, BrainCircuit, ArrowUpRight,
  CalendarDays, Filter, ArrowDown, ArrowUp
} from 'lucide-react'
import { dfcService } from '../../../../services/dfcService'
import { usePermissions, MODULES, ACTIONS } from '../../../../hooks/usePermissions'
import DFCCategoryTransactionsModal from './DFCCategoryTransactionsModal'
import './DFCPage.css'

function DFCPage() {
  const { hasPermission } = usePermissions()
  const canViewDFC = hasPermission(MODULES.FINANCIAL, ACTIONS.FINANCIAL_DFC)
  const [loading, setLoading] = useState(true)
  const [dfcData, setDfcData] = useState(null)
  const [error, setError] = useState(null)
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [selectedAccountId] = useState(null) // Reservado para uso futuro
  const [expandedRows, setExpandedRows] = useState(new Set())
  const [categoryModal, setCategoryModal] = useState(null)

  const months = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
  ]

  const loadDFC = useCallback(async () => {
    if (!canViewDFC) {
      setDfcData(null)
      setLoading(false)
      setError('Você não tem permissão para visualizar o DFC.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const response = await dfcService.getDFC(selectedMonth, selectedYear, selectedAccountId)
      if (response.success) {
        setDfcData(response.data)
      } else {
        setError('Erro ao carregar DFC')
      }
    } catch (err) {
      console.error('Erro ao carregar DFC:', err)
      setError('Erro ao carregar DFC. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }, [selectedMonth, selectedYear, selectedAccountId, canViewDFC])

  useEffect(() => {
    loadDFC()
  }, [loadDFC])

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return 'R$ 0,00'
    return new Intl.NumberFormat('pt-BR', { 
      style: 'currency', 
      currency: 'BRL' 
    }).format(val)
  }

  const toggleRow = (key) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(key)) {
      newExpanded.delete(key)
    } else {
      newExpanded.add(key)
    }
    setExpandedRows(newExpanded)
  }

  const openCategoryModal = (child, flowType) => {
    if (!child?.transactionCount) return
    setCategoryModal({
      category: child,
      flowType
    })
  }

  const closeCategoryModal = () => {
    setCategoryModal(null)
  }

  // Montar estrutura de dados para exibição
  const reportLines = useMemo(() => {
    if (!dfcData) return []

    const lines = []

    // Saldo Inicial
    lines.push({
      key: 'initialBalance',
      label: 'Saldo Inicial do Período',
      value: dfcData.initialBalance || 0,
      isTotal: true,
      isInitial: true
    })

    // Entradas de Caixa
    if (dfcData.inflows) {
      lines.push({
        key: 'inflows',
        label: 'Entradas de Caixa',
        value: dfcData.inflows.total || 0,
        children: dfcData.inflows.categoryBreakdown || [],
        isTotal: false,
        isPositive: true
      })
    }

    // Total de Entradas
    if (dfcData.inflows) {
      lines.push({
        key: 'totalInflows',
        label: 'Total de Entradas',
        value: dfcData.inflows.total || 0,
        isTotal: true,
        isPositive: true
      })
    }

    // Saídas de Caixa
    if (dfcData.outflows) {
      lines.push({
        key: 'outflows',
        label: 'Saídas de Caixa',
        value: dfcData.outflows.total || 0,
        children: dfcData.outflows.categoryBreakdown || [],
        isTotal: false,
        isNegative: true
      })
    }

    // Total de Saídas
    if (dfcData.outflows) {
      lines.push({
        key: 'totalOutflows',
        label: 'Total de Saídas',
        value: dfcData.outflows.total || 0,
        isTotal: true,
        isNegative: true
      })
    }

    // Variação do Caixa
    if (dfcData.cashVariation !== undefined) {
      lines.push({
        key: 'cashVariation',
        label: 'Variação do Caixa',
        value: dfcData.cashVariation || 0,
        isTotal: true,
        isVariation: true
      })
    }

    // Saldo Final
    if (dfcData.finalBalance !== undefined) {
      lines.push({
        key: 'finalBalance',
        label: 'Saldo Final do Período',
        value: dfcData.finalBalance || 0,
        isTotal: true,
        isFinal: true
      })
    }

    return lines
  }, [dfcData])

  const handlePreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12)
      setSelectedYear(selectedYear - 1)
    } else {
      setSelectedMonth(selectedMonth - 1)
    }
  }

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1)
      setSelectedYear(selectedYear + 1)
    } else {
      setSelectedMonth(selectedMonth + 1)
    }
  }

  const getMonthName = () => {
    return `${months[selectedMonth - 1]} ${selectedYear}`
  }

  const getPeriodRange = () => {
    if (!dfcData?.period) return ''
    const start = new Date(dfcData.period.startDate)
    const end = new Date(dfcData.period.endDate)
    return `${start.getDate().toString().padStart(2, '0')} de ${months[start.getMonth()]} a ${end.getDate().toString().padStart(2, '0')} de ${months[end.getMonth()]}`
  }

  if (loading) {
    return (
      <div className="dfc-page">
        <div className="dfc-loading">
          <div className="dfc-spinner"></div>
          <p>Carregando DFC...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="dfc-page">
        <div className="dfc-error">
          <p>{error}</p>
          <button onClick={loadDFC} className="dfc-button-retry">Tentar novamente</button>
        </div>
      </div>
    )
  }

  return (
    <div className="dfc-page">
      {/* Header */}
      <div className="dfc-header">
        <div className="dfc-header-left">
          <div className="dfc-header-title">
            <div className="dfc-header-icon">
              <CalendarDays size={20} />
            </div>
            <h1 className="dfc-title">
              Demonstração do <span className="dfc-title-accent">Fluxo de Caixa</span>
            </h1>
          </div>
          <p className="dfc-subtitle">Movimentações de Caixa por Data de Pagamento/Recebimento</p>
        </div>

        <div className="dfc-header-actions">
          {/* Seletor de Mês */}
          <div className="dfc-month-selector">
            <button 
              onClick={handlePreviousMonth}
              className="dfc-month-button"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="dfc-month-label">
              {getMonthName()}
            </span>
            <button 
              onClick={handleNextMonth}
              className="dfc-month-button"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          <button className="dfc-button-icon">
            <Download size={20} />
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="dfc-kpis">
        <div className="dfc-kpi-card dfc-kpi-primary">
          <div className="dfc-kpi-glow"></div>
          <div className="dfc-kpi-content">
            <p className="dfc-kpi-label">
              <CheckCircle2 size={12} className="dfc-kpi-icon-check" /> Saldo Final do Período
            </p>
            <p className={`dfc-kpi-value ${dfcData?.finalBalance >= 0 ? 'dfc-kpi-positive' : 'dfc-kpi-negative'}`}>
              {formatCurrency(dfcData?.finalBalance || 0)}
            </p>
            <div className={`dfc-kpi-badge ${dfcData?.cashVariation >= 0 ? 'dfc-kpi-badge-success' : 'dfc-kpi-badge-danger'}`}>
              {dfcData?.cashVariation >= 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
              {formatCurrency(Math.abs(dfcData?.cashVariation || 0))} de variação
            </div>
          </div>
          <div className="dfc-kpi-icon">
            <Wallet size={40} />
          </div>
        </div>

        <div className="dfc-kpi-card dfc-kpi-secondary">
          <div className="dfc-kpi-glow dfc-kpi-glow-green"></div>
          <div className="dfc-kpi-content">
            <p className="dfc-kpi-label">
              Entradas de Caixa <Info size={12} className="dfc-kpi-icon-info" />
            </p>
            <p className="dfc-kpi-value dfc-kpi-value-green">
              {formatCurrency(dfcData?.inflows?.total || 0)}
            </p>
            <p className="dfc-kpi-description">
              {dfcData?.summary?.inflowTransactions || 0} transações
            </p>
          </div>
          <div className="dfc-kpi-icon dfc-kpi-icon-green">
            <ArrowUp size={40} />
          </div>
        </div>

        <div className="dfc-kpi-card dfc-kpi-secondary">
          <div className="dfc-kpi-glow dfc-kpi-glow-red"></div>
          <div className="dfc-kpi-content">
            <p className="dfc-kpi-label">
              Saídas de Caixa <Info size={12} className="dfc-kpi-icon-info" />
            </p>
            <p className="dfc-kpi-value dfc-kpi-value-red">
              {formatCurrency(dfcData?.outflows?.total || 0)}
            </p>
            <p className="dfc-kpi-description">
              {dfcData?.summary?.outflowTransactions || 0} transações
            </p>
          </div>
          <div className="dfc-kpi-icon dfc-kpi-icon-red">
            <ArrowDown size={40} />
          </div>
        </div>
      </div>

      {/* Tabela de Composição */}
      <div className="dfc-table-container">
        <div className="dfc-table-header">
          <div className="dfc-table-header-left">
            <h3 className="dfc-table-title">Composição do Fluxo de Caixa</h3>
            <p className="dfc-table-subtitle">
              <Clock size={14} /> Período: {getPeriodRange()}
            </p>
          </div>
          <div className="dfc-table-header-actions">
            <div className="dfc-search">
              <Search className="dfc-search-icon" size={18} />
              <input 
                type="text" 
                placeholder="Localizar categoria..." 
                className="dfc-search-input"
              />
            </div>
            <button className="dfc-button-filter">
              <Filter size={16} /> Filtros
            </button>
          </div>
        </div>

        <div className="dfc-table-content">
          <div className="dfc-table">
            {reportLines.map((line) => (
              <div key={line.key} className="dfc-table-row-group">
                <div 
                  className={`dfc-table-row ${line.isTotal ? 'dfc-table-row-total' : ''} ${line.isFinal ? 'dfc-table-row-final' : ''} ${line.isInitial ? 'dfc-table-row-initial' : ''} ${line.isVariation ? 'dfc-table-row-variation' : ''}`}
                >
                  <div className="dfc-table-row-left">
                    {line.children && line.children.length > 0 ? (
                      <button 
                        onClick={() => toggleRow(line.key)}
                        className="dfc-expand-button"
                      >
                        {expandedRows.has(line.key) ? (
                          <ChevronDown size={18} />
                        ) : (
                          <ChevronRight size={18} />
                        )}
                      </button>
                    ) : (
                      <div className="dfc-expand-spacer"></div>
                    )}
                    <span className={`dfc-table-label ${line.isTotal ? 'dfc-table-label-total' : ''}`}>
                      {line.label}
                    </span>
                  </div>
                  
                  <div className="dfc-table-row-right">
                    <div className="dfc-table-value-container">
                      <span className={`dfc-table-value ${line.isNegative ? 'dfc-table-value-negative' : ''} ${line.isPositive ? 'dfc-table-value-positive' : ''} ${line.isTotal ? 'dfc-table-value-total' : ''} ${line.isFinal ? 'dfc-table-value-final' : ''}`}>
                        {formatCurrency(line.value)}
                      </span>
                    </div>
                    <button className="dfc-table-action">
                      <ArrowUpRight size={20} />
                    </button>
                  </div>
                </div>
                
                {line.children && expandedRows.has(line.key) && (
                  <div className="dfc-table-children">
                    {line.children.map((child, index) => (
                      <div key={index} className="dfc-table-child-row">
                        <div className="dfc-table-child-left">
                          <div className="dfc-table-child-line"></div>
                          <span className="dfc-table-child-label">{child.categoryName || 'Não Categorizado'}</span>
                        </div>
                        <div className="dfc-table-child-right">
                          <span className={`dfc-table-child-value ${child.totalAmount < 0 ? 'dfc-table-value-negative' : ''}`}>
                            {formatCurrency(child.totalAmount)}
                          </span>
                          <button
                            type="button"
                            className="dfc-table-child-count-button"
                            onClick={() => openCategoryModal(child, line.key === 'inflows' ? 'inflow' : 'outflow')}
                            disabled={!child.transactionCount}
                            title="Ver lançamentos da categoria"
                          >
                            ({child.transactionCount} transações)
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Insight Gemini */}
        <div className="dfc-insight">
          <div className="dfc-insight-left">
            <div className="dfc-insight-icon">
              <BrainCircuit size={28} />
            </div>
            <div className="dfc-insight-content">
              <h4 className="dfc-insight-title">Parecer Analítico Gemini</h4>
              <p className="dfc-insight-text">
                A variação de caixa de <span className="dfc-insight-highlight">{formatCurrency(dfcData?.cashVariation || 0)}</span> indica 
                {dfcData?.cashVariation >= 0 ? (
                  <span className="dfc-insight-success"> um fluxo positivo</span>
                ) : (
                  <span className="dfc-insight-danger"> atenção ao fluxo negativo</span>
                )}. 
                O saldo final de {formatCurrency(dfcData?.finalBalance || 0)} sugere {dfcData?.finalBalance >= 0 ? 'boa' : 'necessidade de'} gestão de caixa.
              </p>
            </div>
          </div>
          <button className="dfc-button-pdf">
            Extrair PDF Analítico
          </button>
        </div>
      </div>

      <DFCCategoryTransactionsModal
        isOpen={Boolean(categoryModal)}
        onClose={closeCategoryModal}
        category={categoryModal?.category}
        flowType={categoryModal?.flowType}
        month={selectedMonth}
        year={selectedYear}
        accountId={selectedAccountId}
      />
    </div>
  )
}

export default DFCPage
